<template>
  <v-card flat class="graph-card">
    <v-layout wrap fill-height class="d-flex">
      <v-flex class="pa-0 ma-0 border-radius" white xs12 sm8 md8 lg9>
        <div class="graph-error">
          <Error v-if="errors.length" :errors="errors" />
        </div>
        <v-card tile flat class="ma-3">
          <div class="export">
            <export :data="toExport" :filename="filename">
              <template #trigger>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="primaryLight"
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="toExport.length < 1"
                    >
                      <v-icon size="20px">file_download</v-icon>
                    </v-btn>
                  </template>
                  <span>Download</span>
                </v-tooltip>
              </template>
            </export>
          </div>
          <Loader v-show="loading" class="maximumHeight"></Loader>
          <v-layout align-center wrap>
            <v-flex xs7>
              <div class="d-flex justify-space-between">
                <v-skeleton-loader class="mt-1" v-show="loading" type="text" width="120px" />
              </div>
              <GraphTitle
                v-show="!loading"
                :historical="historical"
                @select="handleSelection"
                @time-ocurred="setTime"
              />
            </v-flex>
            <v-flex :class="!isMobile ? 'pt-1 pr-6' : 'pr-6'" class="d-flex justify-end" xs5>
              <date-picker
                range
                :interval="interval"
                :initialDate="selectedInitialDate"
                v-model="datetime"
              >
                <v-skeleton-loader class="mt-1" v-if="loading" type="text" width="30px" />
                <v-icon v-else-if="isMobile" color="primaryLight">today</v-icon>
                <span v-else class="a-text primaryLight--text d-flex justify-center">{{
                  datetime.text
                }}</span>
              </date-picker>
            </v-flex>
          </v-layout>
          <v-flex>
            <GraphDateBars
              :class="!isMobile ? 'mt-5' : ''"
              :instances="instances"
              :loading="loading"
              :colors="colors"
              :period="period || ''"
              :eu="getEu"
              @toExport="toExport = $event"
            />
          </v-flex>
        </v-card>
      </v-flex>

      <v-flex xs12 sm4 md4 lg3>
        <v-flex fill-height white :class="['border-radius', isMobile ? 'mt-2' : 'ml-2']">
          <GenerationData
            :params="plants"
            :colors="colors"
            featureName="Portfolio"
            componentName="GoalsCard"
            source="plants"
            :instances="allInstancesCopy"
          />
        </v-flex>
      </v-flex>
    </v-layout>
  </v-card>
</template>
<script>
import GraphTitle from "./GraphTitle.vue";
import GraphDateBars from "./GraphDateBars.vue";
import GenerationData from "./Carousel/GenerationData.vue";
import DatePicker from "@/components/DatePickerV2/DatePickerMenu.vue";
import moment from "moment";
import { getComponent, HistoricalArray, validateObject } from "@/models/lib.js";
import { summationByKey } from "@/models/GoalsArray";
import Export from "@/components/XLSX/Export";

export default {
  name: "GraphCard",
  components: {
    GraphTitle,
    DatePicker,
    GraphDateBars,
    GenerationData,
    Error,
    Export,
  },
  props: {
    plants: Array,
  },
  data: () => ({
    component: null,
    config: {
      feature_name: "Portfolio",
      name: "YTD",
    },
    options: {
      energy: "energy",
    },
    interval: "",
    historical: [],
    historicalArray: new HistoricalArray(),
    datetime: { text: "" },
    period: "Year",
    time: {},
    selectedTime: {},
    timeDaily: {},
    timeMonthly: {},
    selectedInitialDate: undefined,
    initialDateDaily: [
      moment().startOf("month").format("YYYY-MM-DD"),
      moment().endOf("month").format("YYYY-MM-DD"),
    ],
    initialDateMonthly: [
      moment().startOf("year").format("YYYY-MM"),
      moment().endOf("year").format("YYYY-MM"),
    ],
    goals: [],
    measurements: [],
    instances: {},
    loaderColor: "#FAFBFD",
    colors: ["#E5927A", "#DE7A8C", "#C26CA6", "#896CBC"],
    loading: true,
    allInstancesCopy: {},
    errors: [],
    toExport: [],
    filename: "Portfolio_" + moment().format("DD-MM-YYYY"),
  }),
  watch: {
    datetime: {
      handler() {
        this.selectDate();
        this.handleTimeSelection();
      },
      deep: true,
      immediate: true,
    },
    plants: {
      immediate: true,
      handler(v) {
        if (Array.isArray(v) && v.length) {
          this.setConfiguration();
        }
      },
    },
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xs;
    },
    getEu() {
      if (this.measurements[0]) {
        const { eu } = this.measurements[0];
        if (eu) return eu;
      }
      return "MWh";
    },
    verifyInstances() {
      return validateObject(this.instances, "measurements");
    },
  },
  methods: {
    timeoutLoading() {
      this.loading = true;
    },

    setTime(value) {
      this.loading = value;
    },

    async setConfiguration() {
      this.timeoutLoading();
      try {
        this.errors = [];
        await this.setBaseConfig();

        const historical = await this.component.setHistorical({
          plants: this.plants,
        });
        await this.setGoals();
        this.$set(this, "historical", historical);
      } catch (error) {
        console.error(error);
        this.errors.push(error);
        this.loading = false;
      }
    },
    async setBaseConfig() {
      const { feature_name, name } = this.config;
      if (this.component) return;
      this.component = await getComponent({ feature_name, name });
      if (!this.component) {
        throw `Component were not found FeatureName: "${feature_name}" Name: "${name}"`;
      }
      /* TODO: allow text configuration
      await this.component.setSpecific();
      this.setTitleTexts();
      */
    },
    async setGoals() {
      const historical = this.component.findHistorical((h) => h && h.name === this.options.energy);
      if (historical) {
        await this.component.setGoals({
          plants: this.plants,
          historical: historical.nameRef,
        });
      }
    },
    async handleSelection(args) {
      if (!args) return;
      const { interval, instances } = args;
      this.historicalArray = instances[interval];
      this.allInstancesCopy = { ...instances };

      if (this.interval === interval) {
        this.handleTimeSelection();
      } else {
        this.interval = interval;
      }
      this.selectDate();
    },

    async handleTimeSelection() {
      this.loading = true;
      if (validateObject(this.selectedTime, ["start", "end"])) {
        await this.fetchData(this.selectedTime);
      }

      this.loading = false;
    },
    selectDate() {
      switch (this.interval) {
        case "daily":
          this.timeDaily = {
            start: this.datetime.startOfDay,
            end: this.datetime.endOfDay,
          };
          this.period = "Month";
          this.selectedInitialDate = this.initialDateDaily;
          this.selectedTime = { ...this.timeDaily };
          break;
        case "monthly":
          this.timeMonthly = {
            start: this.datetime.startOfMonth,
            end: this.datetime.endOfMonth,
          };
          this.period = "Year";
          this.selectedInitialDate = this.initialDateMonthly;
          this.selectedTime = { ...this.timeMonthly };
          break;
      }
    },
    async fetchData(time) {
      this.measurements = await this.historicalArray.setData(time);
      this.goals = await this.component.setGoalsData({
        ...time,
        interval: this.getGoalsInterval(),
      });
      this.instances = {
        measurements: this.measurements,
        goals: summationByKey(this.goals),
        historical: this.historicalArray,
      };
    },

    getGoalsInterval() {
      let period = this.period.toLowerCase();
      const parser = {
        year: "month",
        month: "day",
        week: "week",
        day: "hour",
      };
      return parser[period] || "";
    },
    /**
     * Check if each goal (counted by name) have a record for
     * each current selection of plants, and return a list of goals
     * Which means that a goal line will be shown if all plants
     * have that goal
     * Goals are unique by: Name + PlantId
     *
     * TODO: Verify if that behavior is expected
     * until then function won't be used
     */
    filterGoals(goals) {
      let counter = {};
      goals.forEach((e) => {
        if (!counter[e.name]) {
          counter[e.name] = 0;
        }
        counter[e.name]++;
      });
      let allowed = [];
      for (const name in counter) {
        const i = counter[name];
        if (i == this.plants.length) {
          allowed.push(name);
        }
      }
      return goals.filter((goal) => allowed.includes(goal.name));
    },
  },
};
</script>

<style scoped lang="css">
.graph-card {
  position: relative;
  height: 100%;
  background-color: transparent;
}
.capitalize {
  text-transform: initial;
}
@media only screen and (min-width: 992px) {
  .maximumHeight {
    height: 245px !important;
  }
}
.border-radius {
  border-radius: 4px;
  padding-top: 11px;
  padding-right: 16px;
}
.graph-error {
  position: absolute;
  right: 5px;
  top: 5px;
}
.export {
  position: absolute;
  right: -3px;
  top: 1px;
}
</style>
